// "Atrás" como un árbol en el flujo de compra de la supervisora.
//
// Compras → (Lista de pisos | Modificar compras) → Crear compras → Carrito → confirmar
//
// Al terminar, en vez de dejar Crear compras / Carrito en el historial (y que
// "atrás" vuelva a ellos con el carrito vacío), se rebobina el historial hasta
// Compras (la pantalla desde la que se empezó) y desde ahí se va al destino:
//   - desde Modificar compras → Modificar compras (atrás → Compras)
//   - desde Crear compras     → Compras           (atrás → panel de supervisora)
// Si no se sabe dónde empezó (p. ej. se entró con un enlace o tras recargar en
// otra pantalla), se navega al destino sustituyendo la entrada actual.

const RAIZ = "flujoCompra_raiz"; // {idx, path} de la entrada de Compras
const FIN = "flujoCompra_fin";   // {destino, state} cuando la compra ya se ha confirmado
// Retroceso en curso: si al retroceder el navegador RECARGA la página (entrada de una
// carga anterior, p. ej. tras reabrir la app), el listener se pierde; con esto la app,
// al arrancar, termina el trabajo (ver reanudarFlujoCompra).
const PENDIENTE = "flujoCompra_pendiente"; // {destino, state, volverACompras, t}
// Compra terminada: a partir de aquí "atrás" no vuelve a entrar en sus pantallas
// (ver salidaPorAtras); se quita al empezar otra compra.
const SALIDA = "flujoCompra_salida";

const leer = (k) => {
  try { return JSON.parse(sessionStorage.getItem(k) || "null"); } catch (_) { return null; }
};
const guardar = (k, v) => {
  try { if (v == null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, JSON.stringify(v)); } catch (_) {}
};

// Desde Compras, al pulsar "Crear compras" / "Modificar compras".
export function marcarInicioFlujoCompra() {
  const idx = window.history.state && window.history.state.idx;
  guardar(RAIZ, typeof idx === "number" ? { idx, path: window.location.pathname } : null);
  guardar(FIN, null);
  guardar(SALIDA, null);
}

// Al empezar una compra nueva (entrada normal en Crear compras).
export function empezarCompra() {
  guardar(FIN, null);
  guardar(SALIDA, null);
}

// La compra se ha confirmado: a dónde hay que ir al salir.
export function marcarFinFlujoCompra(destino, state) {
  guardar(FIN, { destino, state: state || null });
}

export const flujoCompraTerminado = () => !!leer(FIN);

// Borrador de "Modificar compras": lo que se va cambiando en Crear compras / Carrito de
// una compra existente y aún NO se ha confirmado. Se guarda en localStorage (sobrevive a
// cerrar la app), se recupera al volver a entrar en esa compra y en la lista de Modificar
// compras se avisa en rojo. Se borra al confirmar, al descartarlo o si la compra ya no
// está en "pedido". Caduca a los 7 días.
const PREFIJO_BORRADOR = "borradorModificar_";
const claveBorrador = (id) => `${PREFIJO_BORRADOR}${id}`;
const CADUCA_BORRADOR_MS = 7 * 24 * 3600 * 1000;
const leerLocal = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || "null"); } catch (_) { return null; }
};
export const borrarBorradorModificar = (id) => {
  if (!id) return;
  try { localStorage.removeItem(claveBorrador(id)); } catch (_) { /* sin storage */ }
  try { sessionStorage.removeItem(claveBorrador(id)); } catch (_) { /* (formato anterior) */ }
};
export const leerBorradorModificar = (id) => {
  if (!id) return null;
  // antes se guardaba en sessionStorage: si queda alguno de esa versión, también vale
  const b = leerLocal(claveBorrador(id)) || leer(claveBorrador(id));
  if (!b || !Array.isArray(b.productos)) return null;
  if (b.ts && Date.now() - b.ts > CADUCA_BORRADOR_MS) { borrarBorradorModificar(id); return null; }
  return b;
};
export const guardarBorradorModificar = (id, datos) => {
  if (!id) return;
  const productos = (datos.productos || []).filter((p) => p && p.cantidad > 0);
  const precio_total = Math.round(productos.reduce((t, p) => t + (Number(p.precio) || 0) * (Number(p.cantidad) || 0), 0) * 100) / 100;
  const anterior = leerBorradorModificar(id) || {};
  try {
    localStorage.setItem(claveBorrador(id), JSON.stringify({
      ...anterior, ...datos, productos, precio_total, n_productos: productos.length, ts: Date.now(),
    }));
  } catch (_) { /* sin storage */ }
};
// Todos los borradores vigentes: [{id, abreviatura, n_productos, precio_total, ts, ...}]
export const listarBorradoresModificar = () => {
  const out = [];
  try {
    const claves = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIJO_BORRADOR)) claves.push(k);
    }
    for (const k of claves) { // (leer puede borrar los caducados: se recorre una copia)
      const id = k.slice(PREFIJO_BORRADOR.length);
      const b = leerBorradorModificar(id);
      if (b) out.push({ ...b, id });
    }
  } catch (_) { /* sin storage */ }
  return out;
};

// ── Mapa del historial: qué pantalla hay en cada posición (history.state.idx) ──
// Lo rellena <RastreoHistorial/> (App.jsx) en cada navegación, se llegue como se
// llegue (clic, atrás/adelante, recarga), así que no depende de por dónde se entró.
const MAPA = "nav_mapa_historial";
// Cada carga de la página tiene su id: solo se retrocede por entradas de ESTA carga.
// Ir con history.go() a una entrada de una carga anterior (p. ej. tras reabrir la app)
// recarga la página: salta el aviso de "perderás los datos" y se pierde el destino.
const CARGA = Math.random().toString(36).slice(2);
export function registrarEntradaHistorial(idx, path, tipo) {
  if (typeof idx !== "number") return;
  const mapa = leer(MAPA) || {};
  if (tipo === "PUSH") {
    Object.keys(mapa).forEach((k) => { if (Number(k) > idx) delete mapa[k]; }); // lo de "adelante" ya no existe
  }
  mapa[idx] = { path, carga: CARGA };
  guardar(MAPA, mapa);
}

const ES_PANTALLA_DEL_FLUJO = /^\/supervisora\/(modificarVerCompras|pisoCrearCompras|crearCompras\/[^/]+|carrito\/[^/]+)\/?$/;
const COMPRAS = "/supervisora/compras";

// Rebobina el recorrido de compra y va al destino guardado: se retrocede por las
// pantallas del flujo (Modificar / lista de pisos / Crear / Carrito) hasta la
// anterior a ellas. Si es Compras, se vuelve a esa misma entrada (y desde ahí al
// destino); si no, se sustituye la primera pantalla del flujo por el destino.
// Si se llama varias veces seguidas (doble clic, efectos repetidos) solo cuenta la primera.
let rebobinando = false;
export function terminarFlujoCompra(navigate, destinoPorDefecto = COMPRAS) {
  // Antes se "rebobinaba" el historial con history.go() hasta Compras. Si la app se
  // había recargado o reabierto a mitad (actualización, Android la cierra…), las
  // entradas anteriores son de otra carga y el salto acababa recargando el carrito
  // en vez de ir a Compras (le pasó a una supervisora el 08/10/2026). Ahora se va
  // directo al destino, sustituyendo la pantalla actual; el "atrás" posterior no
  // vuelve a las pantallas de esta compra gracias a SALIDA (ver salidaPorAtras).
  const fin = leer(FIN) || { destino: destinoPorDefecto, state: null };
  guardar(FIN, null);
  guardar(SALIDA, true);
  guardar(PENDIENTE, null);
  marcarSaliendo();
  navigate(fin.destino, { replace: true, state: fin.state });
}

// Al arrancar la app (RastreoHistorial, App.jsx): si un retroceso de terminarFlujoCompra
// ha acabado recargando la página, se va ahora al destino. Devuelve true si lo ha hecho.
// Durante un momento tras redirigir, las pantallas del flujo no hacen sus propias
// redirecciones (si no, la suya podría pisar a esta).
let saliendoHasta = 0;
const marcarSaliendo = () => { saliendoHasta = Date.now() + 3000; };
export function reanudarFlujoCompra(navigate) {
  const p = leer(PENDIENTE);
  guardar(PENDIENTE, null);
  if (!p || Date.now() - (p.t || 0) > 20000) return false;
  marcarSaliendo();
  if (p.volverACompras) {
    if (p.destino !== COMPRAS) navigate(p.destino, { state: p.state });
    else if (window.location.pathname !== COMPRAS) navigate(COMPRAS, { replace: true });
  } else {
    navigate(p.destino, { replace: true, state: p.state });
  }
  return true;
}
// Para que las pantallas del flujo no hagan sus propias redirecciones mientras tanto
export const flujoCompraReanudado = () => Date.now() < saliendoHasta;

// "Atrás" (móvil o navegador) tras terminar una compra: si va a caer en una pantalla
// de esa compra (crear, carrito, lista de pisos, modificar), se sube al panel de la
// supervisora, como en un árbol. Lo llama RastreoHistorial en cada navegación POP.
export function salidaPorAtras(navigate, path, tipo) {
  if (tipo !== "POP" || !leer(SALIDA) || !ES_PANTALLA_DEL_FLUJO.test(path) || rebobinando) return false;
  marcarSaliendo();
  navigate("/supervisora", { replace: true });
  return true;
}
